import { useState } from 'react';
import axios from 'axios';
import { Lock, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChangePasswordModal({ open, onClose }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCurrentPassword(''); setNewPassword(''); setConfirm('');
    setError(null); setSuccess(false);
  };

  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (newPassword !== confirm) { setError('Passwords don\'t match.'); return; }
    if (newPassword === currentPassword) { setError('New password must be different from current.'); return; }

    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess(true);
      setTimeout(close, 1800);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not change password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={close} data-testid="change-password-modal">
      <div
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-4 right-4 text-text-secondary hover:text-text-primary"
          data-testid="close-password-modal"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-primary-light text-primary flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-text-primary">Change password</h3>
            <p className="text-xs text-text-secondary">For security, you&apos;ll stay logged in on this device.</p>
          </div>
        </div>

        {success ? (
          <div className="flex items-center gap-2 p-4 bg-green-50 text-green-800 rounded-xl text-sm" data-testid="password-change-success">
            <CheckCircle className="w-5 h-5" /> Password changed successfully!
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Current password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="input-field w-full"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="input-field w-full"
                data-testid="new-password-input"
              />
              <p className="text-[11px] text-text-secondary mt-1">At least 8 characters.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Confirm new password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                className="input-field w-full"
                data-testid="confirm-password-input"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="password-change-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="submit-password-change"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Change password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
